import { CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { telemetry } from '../../api/client';
import { promMatrixToChartData } from '../mapping';
import type { ChartPanelConfig } from '../types';
import { PanelEmpty, PanelError, PanelSkeleton } from './chrome';
import { usePanelQuery } from './usePanelQuery';

const PALETTE = ['#f97316', '#38bdf8', '#a78bfa', '#34d399', '#f472b6', '#facc15'];

export interface ChartPanelProps {
  panel: ChartPanelConfig;
  timeRange: string; // dashboard-global (design §3); live panels ignore it, we don't
  refreshMs: number; // dashboard-global auto-refresh; 0 = off
}

export function ChartPanel({ panel, timeRange, refreshMs }: ChartPanelProps) {
  const { data, isLoading, isError, refetch } = usePanelQuery(
    ['telemetry', 'query', panel.metric, panel.serials],
    timeRange,
    refreshMs,
    (w) => telemetry.query({ metric: panel.metric, serials: panel.serials, ...w }),
  );

  if (isLoading) return <PanelSkeleton />;
  if (isError) return <PanelError onRetry={() => void refetch()} />;

  const { keys, data: points } = promMatrixToChartData(data?.series ?? []);
  if (points.length === 0) return <PanelEmpty />;

  return (
    <LineChart data={points} responsive style={{ width: '100%', height: '100%' }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
      <XAxis
        dataKey="ts" type="number" scale="time" domain={['dataMin', 'dataMax']}
        interval="preserveStartEnd" stroke="#71717a" fontSize={10}
        tickFormatter={(ts: number) => new Date(ts).toLocaleTimeString()}
      />
      <YAxis stroke="#71717a" fontSize={10} width={42} />
      <Tooltip labelFormatter={(ts) => new Date(Number(ts)).toLocaleString()} />
      {keys.length > 1 && <Legend wrapperStyle={{ fontSize: 10 }} />}
      {keys.map((k, i) => (
        <Line key={k} type="monotone" dataKey={k} stroke={PALETTE[i % PALETTE.length]} dot={false} isAnimationActive={false} />
      ))}
    </LineChart>
  );
}
